import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Shield, CheckCircle, BookOpen, Lock, AlertCircle, Landmark, Copy, Check, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FEATURES } from '../config/featureFlags';
import { fmtTRY } from '../utils/formatCurrency';
import CourseCover from '../components/course/CourseCover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { courseId } = useParams();
  const { t, getField, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('demo');

  // Wire transfer state
  const [wireInfo, setWireInfo] = useState(null);
  const [wireLoading, setWireLoading] = useState(false);
  const [wireError, setWireError] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await axios.get(`${API}/courses/${courseId}`);
        setCourse(res.data.course);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseId]);

  const fetchWireInfo = useCallback(async () => {
    setWireLoading(true);
    setWireError(false);
    setWireInfo(null);
    try {
      const res = await axios.post(
        `${API}/payment/havale`,
        { course_id: courseId },
        { headers: authHeaders() }
      );
      setWireInfo(res.data);
    } catch (err) {
      if (err?.response?.status === 503) {
        // Banka tanımlanmamış — özel uyarı UI'ı göster
        setWireInfo({ no_bank_configured: true });
      } else {
        setWireError(true);
      }
    } finally {
      setWireLoading(false);
    }
  }, [courseId, authHeaders]);

  useEffect(() => {
    if (paymentMethod === 'wire') {
      fetchWireInfo();
    }
  }, [paymentMethod, fetchWireInfo]);

  const copyToClipboard = (text, fieldKey) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 1800);
    });
  };

  const handlePayment = async () => {
    setProcessing(true);
    setError('');
    try {
      // Demo/quick checkout
      await axios.post(`${API}/payment/checkout`,
        { course_id: courseId },
        { headers: authHeaders() }
      );
      setSuccess(true);
      setTimeout(() => navigate(`/courses/${courseId}`), 2000);
    } catch (err) {
      console.error('Payment error:', err);
      setError(lang === 'tr' ? 'Ödeme işlenirken bir hata oluştu' : 'An error occurred during payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (success) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]" data-testid="payment-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card-clay p-12 text-center max-w-md"
      >
        <div className="w-20 h-20 rounded-full bg-[var(--brand-success)] flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} strokeWidth={2.5} className="text-white" />
        </div>
        <h2 className="font-display text-2xl font-bold mb-2">
          {lang === 'tr' ? 'Ödeme Başarılı!' : 'Payment Successful!'}
        </h2>
        <p className="text-[var(--text-muted)] font-medium">
          {lang === 'tr' ? 'Kursa yonlendiriliyorsunuz...' : 'Redirecting to course...'}
        </p>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-app)] py-12" data-testid="checkout-page">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="font-display text-3xl font-bold mb-8 text-center">
          {lang === 'tr' ? 'Ödeme' : 'Checkout'}
        </h1>
        
        <div className="grid gap-6">
          {/* Order Summary */}
          {course && (
            <div className="card-clay p-6" data-testid="order-summary">
              <h3 className="font-display font-bold text-lg mb-4">{lang === 'tr' ? 'Sipariş Özeti' : 'Order Summary'}</h3>
              <div className="flex gap-4">
                <CourseCover
                  thumbnail={course.thumbnail}
                  category={course.category}
                  alt={getField(course, 'title')}
                  className="w-24 h-16 rounded-xl shrink-0"
                  iconSize={24}
                />
                <div className="flex-1">
                  <h4 className="font-bold text-sm">{getField(course, 'title')}</h4>
                  <p className="text-xs text-[var(--text-muted)] font-medium">{course.instructor_name}</p>
                </div>
                <p className="font-display font-bold text-lg text-[var(--brand-primary)]">
                  {course.price} {course.currency || 'TRY'}
                </p>
              </div>
              <div className="border-t border-[var(--border-light)] mt-4 pt-4 flex justify-between items-center">
                <span className="font-bold">{lang === 'tr' ? 'Toplam' : 'Total'}</span>
                <span className="font-display text-2xl font-bold text-[var(--brand-primary)]">
                  {course.price} {course.currency || 'TRY'}
                </span>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="card-clay p-6" data-testid="payment-method-section">
            <h3 className="font-display font-bold text-lg mb-4" id="payment-method-group-label">{t('checkout.payment_method_group')}</h3>

            <div role="radiogroup" aria-labelledby="payment-method-group-label" className="space-y-3 mb-6">
              <button
                role="radio"
                aria-checked={paymentMethod === 'demo'}
                onClick={() => setPaymentMethod('demo')}
                className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                  paymentMethod === 'demo'
                    ? 'border-[var(--brand-secondary)] bg-[#FFF4CC]'
                    : 'border-[var(--border-light)] hover:border-gray-300'
                }`}
                data-testid="payment-demo-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <BookOpen size={20} className="text-yellow-600" />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-bold text-sm">{lang === 'tr' ? 'Hızlı Ödeme (Demo)' : 'Quick Pay (Demo)'}</span>
                  <p className="text-[10px] text-[var(--text-muted)] font-medium">
                    {lang === 'tr' ? 'Kart bilgisi girmeden devam et' : 'Continue without card details'}
                  </p>
                </div>
                {paymentMethod === 'demo' && (
                  <CheckCircle size={20} className="text-[var(--brand-secondary)]" />
                )}
              </button>

              {/* Havale / EFT */}
              <button
                role="radio"
                aria-checked={paymentMethod === 'wire'}
                onClick={() => setPaymentMethod('wire')}
                className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                  paymentMethod === 'wire'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-[var(--border-light)] hover:border-gray-300'
                }`}
                data-testid="payment-wire-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Landmark size={20} className="text-emerald-600" />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-bold text-sm">{t('checkout.method_wire')}</span>
                  <p className="text-[10px] text-[var(--text-muted)] font-medium">
                    {t('checkout.method_wire_sub')}
                  </p>
                </div>
                {paymentMethod === 'wire' && (
                  <CheckCircle size={20} className="text-emerald-500" />
                )}
              </button>
            </div>
          </div>

          {/* Wire Transfer Info Panel */}
          <AnimatePresence>
            {paymentMethod === 'wire' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {wireLoading && (
                  <div className="flex items-center justify-center gap-2 py-6 text-[var(--text-muted)]">
                    <div className="w-5 h-5 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium">{t('checkout.wire_loading')}</span>
                  </div>
                )}

                {wireError && !wireLoading && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                    <AlertCircle size={20} className="text-red-500 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-700">{t('checkout.wire_error')}</p>
                    </div>
                    <button
                      onClick={fetchWireInfo}
                      className="flex items-center gap-1 text-xs font-bold text-red-600 border border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-100"
                    >
                      <RefreshCw size={12} /> {t('checkout.wire_retry')}
                    </button>
                  </div>
                )}

                {wireInfo && !wireLoading && (
                  <>
                    {wireInfo.no_bank_configured ? (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle size={20} className="text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-sm font-bold text-amber-700">{t('checkout.wire_no_bank')}</p>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5 space-y-4">
                        <h4 className="font-bold text-emerald-800 flex items-center gap-2">
                          <Landmark size={18} className="text-emerald-600" />
                          {t('checkout.wire_ref_title')}
                        </h4>

                        {/* Warning notice */}
                        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 flex items-start gap-2">
                          <AlertCircle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                          <div className="text-xs">
                            <p className="font-bold text-amber-700">{t('checkout.wire_notice')}</p>
                            <p className="text-amber-600 mt-0.5">{t('checkout.wire_notice_sub')}</p>
                          </div>
                        </div>

                        {/* Bank details */}
                        {/* aria-live region — ekran okuyuculara "kopyalandı" bilgisi */}
                        <span
                          aria-live="polite"
                          aria-atomic="true"
                          className="sr-only"
                        >
                          {copiedField ? t('checkout.wire_copied') : ''}
                        </span>
                        <div className="space-y-2">
                          {[
                            { labelKey: 'checkout.wire_bank_name', value: wireInfo.bank_info?.bank_name },
                            { labelKey: 'checkout.wire_iban', value: wireInfo.bank_info?.iban, mono: true },
                            { labelKey: 'checkout.wire_account_no', value: wireInfo.bank_info?.account_no, mono: true },
                            { labelKey: 'checkout.wire_ref_no', value: wireInfo.payment_ref, mono: true, highlight: true },
                            { labelKey: 'checkout.wire_amount', value: wireInfo.amount ? fmtTRY(wireInfo.amount) : null },
                          ].filter(row => row.value).map(row => (
                            <div key={row.labelKey} className="flex items-center justify-between gap-2 py-2 border-b border-emerald-200 last:border-0">
                              <span className="text-xs font-bold text-emerald-700 shrink-0 w-28">{t(row.labelKey)}</span>
                              <span className={`flex-1 text-xs font-semibold ${row.mono ? 'font-mono' : ''} ${row.highlight ? 'text-[var(--brand-primary)] text-sm font-bold' : 'text-[var(--text-main)]'}`}>
                                {row.value}
                              </span>
                              {row.value && (
                                <button
                                  onClick={() => copyToClipboard(row.value, row.labelKey)}
                                  aria-label={t('checkout.wire_copy')}
                                  className="p-1.5 rounded-lg hover:bg-emerald-200 text-emerald-600 shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                >
                                  {copiedField === row.labelKey
                                    ? <Check size={14} className="text-green-600" />
                                    : <Copy size={14} />}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        {wireInfo.instructions && (
                          <p className="text-xs text-emerald-700 font-medium">{wireInfo.instructions}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3"
              data-testid="payment-error"
            >
              <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
              <p className="text-sm font-bold text-red-700">{error}</p>
            </motion.div>
          )}

          {/* Trust Signals */}
          <div className="flex items-center justify-center gap-6 text-sm text-[var(--text-muted)]">
            <div className="flex items-center gap-1">
              <Shield size={16} strokeWidth={2.5} /> {lang === 'tr' ? 'Güvenli Ödeme' : 'Secure Payment'}
            </div>
            <div className="flex items-center gap-1">
              <Lock size={16} strokeWidth={2.5} /> SSL 256-bit
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle size={16} strokeWidth={2.5} /> {lang === 'tr' ? '30 Gun Garanti' : '30 Day Guarantee'}
            </div>
          </div>

          {/* Feature flag: Satın alma gecici olarak devre disi */}
          {!FEATURES.PURCHASE_ENABLED && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-center gap-3"
              data-testid="purchase-disabled-notice"
            >
              <AlertCircle size={20} className="text-amber-500 flex-shrink-0" />
              <p className="text-sm font-bold text-amber-700">
                {lang === 'tr'
                  ? 'Satın alma geçici olarak devre dışı. Yakında aktif edilecek.'
                  : 'Purchasing is temporarily disabled. Coming soon.'}
              </p>
            </motion.div>
          )}

          {/* Pay Button — wire transfer'de sadece banka bilgileri gösterilir, buton gizlenir */}
          {paymentMethod !== 'wire' && (
          <button
            onClick={FEATURES.PURCHASE_ENABLED ? handlePayment : undefined}
            disabled={!FEATURES.PURCHASE_ENABLED || processing}
            className={`w-full btn-success py-4 text-lg flex items-center justify-center gap-2${!FEATURES.PURCHASE_ENABLED || processing ? ' opacity-50 cursor-not-allowed' : ''}`}
            title={!FEATURES.PURCHASE_ENABLED ? (lang === 'tr' ? 'Yakında' : 'Coming soon') : undefined}
            data-testid="pay-btn"
          >
            {processing
              ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {lang === 'tr' ? 'Isleniyor...' : 'Processing...'}
                </span>
              )
              : !FEATURES.PURCHASE_ENABLED
              ? (
                <span className="flex items-center gap-2">
                  <Lock size={18} />
                  {lang === 'tr' ? 'Yakında' : 'Coming Soon'}
                </span>
              )
              : (
                <span className="flex items-center gap-2">
                  <Lock size={18} />
                  {lang === 'tr' ? 'Guvenle Ode' : 'Pay Securely'} {course?.price} {course?.currency || 'TRY'}
                </span>
              )}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
